import { useState } from "react";
import { FileDown, Printer, Send } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Btn } from "@/components/Btn";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errText } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { SentReports, useSentReports } from "@/components/SentReports";
import { useAuth } from "@/lib/auth";
import { useCards, usePrefs } from "@/lib/store";
import { downloadPdf, pdfDataUri, printReport } from "@/lib/report";

const RANGES = [["7", "Last 7 days"], ["30", "Last 30 days"], ["90", "Last 90 days"], ["all", "All time"]];
const TYPES = [["all", "Clinical Summary"], ["triggers", "Trigger Log"], ["moods", "Mood Log"]];

const Chips = ({ options, value, onChange, prefix }) => (
  <div className="flex flex-wrap gap-2">
    {options.map(([k, l]) => (
      <button key={k} onClick={() => onChange(k)} data-testid={`${prefix}-${k}`}
        className={`px-4 h-9 rounded-full text-sm font-semibold border transition-colors ${value === k ? "bg-t-brand text-t-onbrand border-t-brand" : "bg-t-surface2 text-t-on2 border-t-border"}`}>{l}</button>
    ))}
  </div>
);

export function ExportPanel({ open, onOpenChange, sessions, flips }) {
  const { user } = useAuth();
  const { data: prefs } = usePrefs();
  const { data: cards = [] } = useCards();
  const [range, setRange] = useState("30");
  const [type, setType] = useState("all");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const qc = useQueryClient();
  const to = prefs?.therapistEmail?.trim();
  const who = prefs?.therapistName?.trim() || "your psychologist";
  const rangeLabel = RANGES.find((r) => r[0] === range)[1];

  const pick = (rk = range, tk = type) => {
    const from = rk === "all" ? 0 : Date.now() - Number(rk) * 86400000;
    return sessions.filter((s) => new Date(s.timestamp).getTime() >= from && (tk === "all" || (tk === "triggers" ? s.triggered : s.moodNow != null)));
  };
  const prep = prefs?.sessionNote?.trim();
  const { data: sent = [] } = useSentReports(open);
  const notes = sent.flatMap((r) => (r.notes || []).map((n) => ({ ...n, title: r.title, who: r.therapistName }))).sort((a, b) => a.at.localeCompare(b.at)).slice(-20);
  const opts = () => ({ clientName: user?.name, avatar: user?.picture, title: TYPES.find((t) => t[0] === type)[1], prepNote: prep, weeklySummary: prefs?.weeklySummary?.text, notes });
  const resend = (r) => {
    const rk = r.rangeKey ?? RANGES.find((x) => x[1] === r.period)?.[0];
    const tk = r.typeKey ?? TYPES.find((x) => x[1] === r.title)?.[0];
    if (!rk || !tk) return toast.error("Couldn't read that report's settings — choose them above instead");
    setRange(rk);
    setType(tk);
    if (!pick(rk, tk).length) return empty();
    setConfirm(true);
  };

  const empty = () => toast.error(sessions.length ? "No entries match that range/type" : "No entries to export yet");

  const run = async (kind) => {
    const list = pick();
    if (!list.length) return empty();
    setBusy(kind);
    try {
      if (kind === "print") printReport(list, flips, cards, opts());
      else await downloadPdf(list, flips, cards, opts());
      if (kind === "download") toast.success("PDF downloaded");
    } catch {
      toast.error("Couldn't create the PDF");
    } finally {
      setBusy(false);
    }
  };

  const askSend = () => (pick().length ? setConfirm(true) : empty());

  const send = async () => {
    setConfirm(false);
    setBusy("send");
    try {
      const pdf = await pdfDataUri(pick(), flips, cards, opts());
      const { data } = await api.post("/reports/send", { pdf, title: opts().title, period: rangeLabel, note: note.trim() || null, rangeKey: range, typeKey: type });
      toast.success(`Report sent securely to ${data.to}`);
      setNote("");
      qc.invalidateQueries({ queryKey: ["sent-reports"] });
      qc.invalidateQueries({ queryKey: ["prefs"] });
    } catch (e) {
      toast.error(e?.response ? errText(e) : "Couldn't create the PDF");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl bg-t-surface border-t-border max-w-lg max-h-[90vh] overflow-y-auto" data-testid="export-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl text-t-on">Export options</DialogTitle>
          <DialogDescription className="text-t-muted">A professional PDF summary for your psychologist or psychiatrist.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-5">
          <div><p className="tf-tag mb-2">Period</p><Chips options={RANGES} value={range} onChange={setRange} prefix="export-range" /></div>
          <div><p className="tf-tag mb-2">Report</p><Chips options={TYPES} value={type} onChange={setType} prefix="export-type" /></div>
          <div>
            <p className="tf-tag mb-2">Psychologist email</p>
            <p className="text-sm text-t-on2" data-testid="export-therapist-email">
              {to || <>Not set yet — <Link to="/settings" className="text-t-brand font-semibold underline" data-testid="export-settings-link">add it in Settings</Link> to send reports in one tap.</>}
            </p>
            {prep ? <p className="mt-2 text-sm rounded-2xl bg-t-brand3 px-4 py-2.5 text-t-on2" data-testid="export-prep-included">Your <b>Next session</b> note is included in this report.</p> : null}
            <textarea className="tf-input mt-3 !min-h-[72px]" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a short note (optional), e.g. Rough week — keen to talk about it on Thursday." data-testid="export-note-input" />
          </div>
          <div className="flex flex-col gap-2.5">
            <Btn icon={<FileDown size={18} />} label="Download PDF" loading={busy === "download"} onClick={() => run("download")} testId="export-download-button" />
            <Btn variant="secondary" icon={<Send size={18} />} label={busy === "send" ? "Sending securely…" : "Send to my psychologist"} loading={busy === "send"} disabled={!to || !!busy} onClick={askSend} testId="export-send-button" />
            <Btn variant="ghost" icon={<Printer size={18} />} label="Print / Save as PDF" onClick={() => run("print")} testId="export-print-button" />
          </div>
          <SentReports open={open} onResend={resend} busy={!!busy} />
        </div>
      </DialogContent>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="rounded-3xl bg-t-surface border-t-border" data-testid="send-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-t-on">Send this report?</AlertDialogTitle>
            <AlertDialogDescription className="text-t-on2" data-testid="send-confirm-text">
              Your {opts().title} ({rangeLabel.toLowerCase()}) will be emailed to {who} at <b>{to}</b> as a secure link that expires in 14 days.{prep ? " Your Next session note is included and will then be cleared." : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="send-cancel-button">Not now</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-t-brand text-t-onbrand hover:opacity-90" onClick={send} data-testid="send-confirm-button">Yes, send it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
